import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { getComputedLineHeight } from 'chaoskit/src/assets/styles/utility/misc'
import { Badge } from 'chaoskit/src/components'
import { StylesBadgeVariables } from 'chaoskit/src/components/Badge'
import { kebabCase, toLower } from 'lodash'

import Data from '~components/Data'
import Icon from '~components/Icon'

const ReleaseTag = ({ variation }) => {
  const theme = useTheme()

  let type

  // eslint-disable-next-line default-case
  switch (variation) {
    case 'info':
      type = 'info'
      break
    case 'new':
      type = 'primary'
      break
    case 'fix':
      type = 'default'
      break
  }

  return (
    <Badge
      css={{
        justifyContent: 'center',
        position: 'relative',
        top:
          (getComputedLineHeight({
            fontSize: theme.fontSize.base,
            lineHeight: theme.lineHeight.base,
          }) -
            StylesBadgeVariables(theme).height) /
          2,
      }}
      type={type}
    >
      {variation}
    </Badge>
  )
}

ReleaseTag.propTypes = {
  variation: PropTypes.oneOf(['info', 'fix', 'new']),
}

const ReleasesList = ({ edges }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: theme.space.large + theme.space.base,
        position: 'relative',
        paddingTop: theme.space.base,
        marginBottom: theme.space.large,
      }}
    >
      <div
        css={{
          position: 'absolute',
          zIndex: 0,
          background: theme.color.border.base,
          top: 0,
          height: '100%',
          width: theme.space.xsmall,
          borderRadius: 9999,
          left: theme.space.base / 2 - theme.space.xsmall / 2,
        }}
      />
      {edges.map(({ node }) => {
        const id = kebabCase(toLower(node.frontmatter.title))

        return (
          <div
            key={node.frontmatter.title}
            css={{
              display: 'grid',
              gridAutoColumns: 'auto',
              justifyContent: 'start',
              gridAutoFlow: 'column',
              alignItems: 'start',
              gap: theme.space.base,
              position: 'relative',
              zIndex: 1,

              '&:hover, &:focus': {
                '.AutoLinkHeader__Link': {
                  display: 'block',
                },

                '.ReleasesList__Bullet': {
                  display: 'none',
                },
              },
            }}
          >
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                ...theme.text.large,
              }}
            >
              &#8203;
              <div
                css={{
                  width: theme.space.base,
                  height: theme.space.base,
                  borderRadius: 9999,
                  background: theme.color.border.base,
                  boxShadow: '0 0 0 4px #fff',
                }}
                className="ReleasesList__Bullet"
              />
              <a
                href={`#${id}`}
                css={{
                  display: 'none',
                  background: theme.color.light.base,
                  ...theme.text.base,
                  height: theme.fontSize.base,
                  lineHeight: 1,
                  boxShadow: '0 0 0 4px #fff',
                  color: theme.fontColor.base,
                }}
                className="AutoLinkHeader__Link"
              >
                <Icon icon="anchor" />
              </a>
            </div>
            <div>
              <header>
                <div
                  css={{
                    ...theme.text.large,
                    marginBottom: 0,
                    display: 'inline-grid',
                    gridAutoColumns: 'auto',
                    gridAutoFlow: 'column',
                    justifyContent: 'start',
                    alignItems: 'baseline',
                    gap: theme.space.base,
                  }}
                  id={id}
                >
                  <span
                    css={{
                      fontFamily: theme.fontFamily.mono,
                      fontWeight: theme.fontWeight.bold,
                      letterSpacing: theme.letterSpacing.negative,
                    }}
                  >
                    {node.frontmatter.title}
                  </span>
                  <div
                    css={{
                      color: theme.fontColor.muted,
                      ...theme.text.small,
                    }}
                  >
                    {node.frontmatter.date}
                  </div>
                </div>
              </header>
              <div
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr)',
                  columnGap: theme.space.small,
                  rowGap: theme.space.base,
                  marginTop: theme.space.base,
                }}
              >
                {node.frontmatter.notes.map((note) => (
                  <Fragment key={note.note}>
                    <ReleaseTag variation={note.type} />
                    <div>
                      <Data>{note.note}</Data>
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

ReleasesList.propTypes = {
  edges: PropTypes.array.isRequired,
}

export default ReleasesList
